import { useState } from 'preact/hooks';
import { startDeploymentCheckout } from '../api/billing';
import { getBrand } from '../brands';
import { VatNotice } from '../shared-ui/VatNotice';

interface Props {
  /** Called when the user dismisses or the checkout redirect fires */
  onClose: () => void;
}

/**
 * Phase 3 of plans/browser-mcp-oauth-custody-fix.md — shown when the user
 * triggers browser intent but their tier doesn't include the browser tool.
 * Triggered by the BROWSER_TIER_GATE WebSocket event.
 */
export function BrowserTierGateModal({ onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleUpgrade() {
    setLoading(true);
    setError(null);
    try {
      // Buy-loop §D2: mint a signed buy-intent for this deployment's brand (resolved server-side)
      // → redirect to the inctasoft-cf edge checkout (or the Customer Portal if already subscribed).
      const { url } = await startDeploymentCheckout('pro_studio');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upgrade checkout is not available yet. Please contact support.');
      setLoading(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '16px',
    }}>
      <div style={{
        background: 'var(--bg-card, #1e1e2e)',
        border: '1px solid var(--border, #313244)',
        borderRadius: '12px',
        maxWidth: '480px',
        width: '100%',
        padding: '28px 24px',
        color: 'var(--fg, #cdd6f4)',
        maxHeight: '90svh',
        overflowY: 'auto',
      }}>
        <h2 style={{ margin: '0 0 12px', fontSize: '1.2rem', fontWeight: 700 }}>
          Browser tool is part of {getBrand().name} Studio
        </h2>

        <p style={{ margin: '0 0 16px', fontSize: '0.9rem', lineHeight: 1.5 }}>
          The agent's browser runs on our servers with your profile persisted so you stay
          logged in across sessions. It's available on the <b>Studio</b> tier.
        </p>

        <ul style={{ margin: '0 0 20px 0', padding: '0 0 0 20px', fontSize: '0.875rem', lineHeight: 1.55, color: 'var(--fg-muted, #a6adc8)' }}>
          <li>Agent can open pages, fill forms, navigate sign-in flows</li>
          <li>Profile persists across sessions — wipe anytime from Settings</li>
          <li>Includes everything in Pro ($9.99/mo) plus the browser tool</li>
        </ul>

        {/* Stripe Tax P3: the VAT basis is stated at the Upgrade buy button — a notice, not a gate (SE-03). */}
        <div style={{ marginBottom: '12px' }}>
          <VatNotice />
        </div>

        {error && (
          <div style={{ color: 'var(--red, #f38ba8)', fontSize: '0.85rem', marginBottom: '12px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid var(--border, #313244)',
              background: 'transparent',
              color: 'var(--fg, #cdd6f4)',
              fontWeight: 600,
              fontSize: '0.9rem',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Not now
          </button>
          <button
            onClick={() => handleUpgrade()}
            disabled={loading}
            style={{
              flex: 1.4,
              padding: '12px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--accent, #89b4fa)',
              color: '#1e1e2e',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Loading…' : 'Upgrade'}
          </button>
        </div>
      </div>
    </div>
  );
}
